import React, { createContext, useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import { getToken } from "../utils/tokenUtils";

// Create the employeeGradesMasterContext
const EmployeeGradesMasterContext = createContext();

// Custom hook to use the employeeGrades context
export const useEmployeeGrade = () => useContext(EmployeeGradesMasterContext);

// Provider component to wrap the app
export const EmployeeGradeMasterProvider = ({ children }) => {
  const [employeeGrades, setEmployeeGrades] = useState([]);
  const API_URL = "https://hrmspayroll.prepstripe.com/api/masters/emp-grades"; // Replace with your actual API URL
  const token = getToken();
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 10,
  });
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchEmployeeGrades = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}?page=${page}`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      const { data: paginationData } = response.data;
      const { data, current_page, last_page, per_page, total } = paginationData;

      setEmployeeGrades(data || []);

      setPagination({
        currentPage: current_page,
        totalPages: last_page,
        totalItems: total,
        perPage: per_page,
      });
      setLoading(false);
    } catch (error) {
      console.error("Error fetching employee grades master:", error);
      setError(error.response?.data?.message || "Failed to fetch employee grades.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch employee grade master from the API on load
  useEffect(() => {
    fetchEmployeeGrades();
  }, [API_URL, token]);

  // Add a new employee grade
  const addEmployeeGrade = async (newEmployeeGrade) => {
    try {
      const response = await axios.post(API_URL, newEmployeeGrade, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      // setEmployeeGrades((prev) => [...prev, response.data?.data]);
      setEmployeeGrades((prev) => [response.data?.data, ...prev]);

    } catch (error) {
      console.error("Error adding employee grades:", error);
    }
  };

  // Update an existing employee grade
  const updateEmployeeGrade = async (updatedEmployeeGrade) => {
    // console.log("Updating employee grade with data:", updatedEmployeeGrade);
    try {
      const response = await axios.put(`${API_URL}/${updatedEmployeeGrade.id}`, updatedEmployeeGrade, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      setEmployeeGrades((prev) =>
        prev.map((employeeGrade) =>
          employeeGrade.id === updatedEmployeeGrade.id ? { ...employeeGrade, ...response.data?.data } : employeeGrade
        )
      );
    } catch (error) {
      console.error("Error updating employee grade master:", error);
    }
  };

  // Delete a employee grade
  const deleteEmployeeGrade = async (id) => {
    try {
      await axios.delete(`${API_URL}/${id}`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      setEmployeeGrades((prev) => prev.filter((employeeGrade) => employeeGrade.id !== id));
    } catch (error) {
      console.error("Error deleting employee Grade:", error);
    }
  };

  // Memoize the context value
  const value = useMemo(
    () => ({
      isLoading,
      error,
      pagination,
      employeeGrades,
      fetchEmployeeGrades,
      addEmployeeGrade,
      updateEmployeeGrade,
      deleteEmployeeGrade,
    }),
    [employeeGrades, isLoading, pagination] // Recalculate the value only when `employeeGrade` changes
  );

  return <EmployeeGradesMasterContext.Provider value={value}>{children}</EmployeeGradesMasterContext.Provider>;
};
